import { defineComponent as _defineComponent } from 'vue'
import {
  computed, nextTick, onBeforeUnmount, ref,
} from 'vue';
import { v4 as uuidv4 } from 'uuid';
import {
  SPaymentTypeEnum, STripTypeEnum, SCheckoutErrorsEnum, SOnBoardPaymentErrorDialog, SOrderService,
  SServerErrorDialog, SMobileBookingSummary, SCheckoutBookingSummary, SVehicleSearchResultDialog, SPaymentDialog, SUserService,
} from '@slg/web-customer-shared';
import PassengerInfo from '@/components/checkout/passengerInfo/PassengerInfo.vue';
import { loadStripe } from '@stripe/stripe-js';
import { resolveReservationError } from './checkout.logic';
import {
  useStore, useRouter, useRouteRef, useVuetify, useVm, useGlobalErrorDialog, useGtm,
} from '@/composables';


const __default__ = {
  name: 'Checkout',
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const store = useStore();
const router = useRouter();
const route = useRouteRef();
const vuetify = useVuetify();
const vm = useVm();
const { setGlobalError } = useGlobalErrorDialog();
const { trackGtmEvent, trackGtmEcommerceEvent } = useGtm();

const isLoading = ref(true);
const isAgree = ref(false);
const isAnotherDateSelectionDialogOpen = ref(false);
const isTripDetailsOpen = ref(false);
const isNeededVehicleFound = ref<boolean | null>(null);
const isOutwardVehicleFound = ref<boolean | null>(null);
const isReturnVehicleFound = ref<boolean | null>(null);
const isPaymentDialogOpen = ref(false);
const isOnBoardPaymentAvailableDialogOpen = ref(false);
const isServerErrorDialogOpen = ref(false);
const paymentInfo = ref<any>(null);
const validNightCards = ref<any[]>([]);
const checkOrderStateIntervalId = ref<ReturnType<typeof setInterval> | null>(null);
const getTripType = computed(() => store.getters['booking/getTripType']);
const getAuthToken = computed(() => store.getters['auth/getAuthToken']);
const isFreeTrip = computed(() => store.getters['opsZone/isFreeTrip']);
const getSelectedRides = computed(() => store.getters['booking/getSelectedRides']);
const getPaymentType = computed(() => store.getters['booking/getPaymentType']);
const getPaymentMethod = computed(() => store.getters['booking/getPaymentMethod']);
const getIsFreeTrip = computed(() => store.getters['booking/getIsFreeTrip']);
const getBookingRequestId = computed(() => store.getters['booking/getBookingRequestId']);
const getPassengerPhone = computed(() => store.getters['booking/getPassengerPhone']);
const getPassengerFirstName = computed(() => store.getters['booking/getPassengerFirstName']);
const getPassengerLastName = computed(() => store.getters['booking/getPassengerLastName']);
const getPassengerEmail = computed(() => store.getters['booking/getPassengerEmail']);
const getUserDetailsForBookingDetails = computed(() => store.getters['auth/getUserDetailsForBookingDetails']);
const getCheckoutInfo = computed(() => store.getters['booking/getCheckoutInfo']);
const getOutwardState = computed(() => store.getters['booking/getOutwardState']);
const getReturnState = computed(() => store.getters['booking/getReturnState']);
const getReservationId = computed(() => store.getters['booking/getReservationId']);
const isAnotherPassenger = computed(() => store.getters['booking/isAnotherPassenger']);
const getAnotherPassengerList = computed(() => store.getters['auth/getAnotherPassengerList']);
const getBookingDetails = computed(() => store.getters['booking/getBookingDetails']);
const isRoundTrip = computed(() => getTripType.value === STripTypeEnum.ROUND_TRIP);
const isExtraSmall = computed(() => vuetify.breakpoint.xs);
const isCheckoutPage = computed(() => route.value.name === 'Checkout');
const getTotalPrice = computed(() => (
  Number(getBookingDetails.value?.originalOutwardPrice) + Number(getBookingDetails.value?.originalReturnPrice)
));

function onTrackEventNoVehiclesFound() {
  trackGtmEvent('The vehicle is not found dialog ok button click', 'Button', 'Click', 'Redirect to home page');
}

function onTrackEventServerErrorOccurred() {
  trackGtmEvent('Server error occurred dialog ok button', 'Button', 'Click', 'Redirect to home page');
}

function onTrackEventPayButtonClick() {
  trackGtmEvent('Pay button click', 'Button', 'Click', 'Confirm online payment');
}

function updateNightCards(cards: any[]) {
  validNightCards.value = cards;
}

async function paymentCompleted() {
  const { orderId } = paymentInfo.value;

  trackGtmEvent(
    'Booking completed successfully',
    'Special event',
    '',
    'Booking completed successfully, redirect to confirmation page',
  );
  store.commit('booking/clearTemporaryReservation');
  await router.push({ name: 'Confirmation', params: { orderId } });
}

async function subscribeOnPaymentComplete() {
  const { orderId } = paymentInfo.value;
  store.commit('common/setGlobalLoading', true);
  try {
    checkOrderStateIntervalId.value = setInterval(async () => {
      const checkoutOrder = await SOrderService.getOrder(orderId);

      if (checkoutOrder?.data?.orderState === SOrderService.SOrderStateEnum.BOOKED) {
        clearInterval(checkOrderStateIntervalId.value as ReturnType<typeof setInterval>);
        store.commit('common/setGlobalLoading', false);
        paymentCompleted();
      }
    }, 2000);
  } catch (error: any) {
    store.commit('common/setGlobalLoading', false);
    if (error.status !== 401) {
      await setGlobalError(error, false);
    }
  }
}

async function onPaymentSuccess(data: any) {
  store.commit('booking/setIgnoreRemainingTime', true);
  isPaymentDialogOpen.value = false;
  const { orderId } = paymentInfo.value;

  store.commit('common/setGlobalLoading', true);
  try {
    if (getPaymentType.value === SPaymentTypeEnum.PRE_PAID_STRIPE) {
      await subscribeOnPaymentComplete();
    } else {
      const bookingCheckoutOrderModel = getPaymentType.value !== SPaymentTypeEnum.PRE_AUTHORIZATION
        ? {
          paymentMethodNonce: data.nonce,
          orderId,
        }
        : {
          paymentMethodId: getPaymentMethod.value,
          orderId,
        };
      await SOrderService.bookingCheckoutOrder(bookingCheckoutOrderModel);
      trackGtmEvent(
        'Booking completed successfully',
        'Special event',
        '',
        'Booking completed successfully, redirect to confirmation page',
      );
      store.commit('booking/clearTemporaryReservation');
      store.commit('common/setGlobalLoading', false);
      await router.push({ name: 'Confirmation', params: { orderId } });
    }
  } catch (e: any) {
    store.commit('common/setGlobalLoading', false);
    if (e.status !== 401) {
      await setGlobalError(e, false);
    }
  }
  store.commit('booking/setIgnoreRemainingTime', false);
}

function createReservation(requestDelay: number, maxRequestDelay: number, spinnerText: string) {
  store.commit('common/setLabel', spinnerText);
  setTimeout(async () => {
    await store.dispatch('booking/setTemporaryReservation', getBookingRequestId.value)
      .then(() => {
        isNeededVehicleFound.value = true;
        isAnotherDateSelectionDialogOpen.value = true;
        trackGtmEcommerceEvent('add_to_cart', getTotalPrice.value, isRoundTrip.value);
        setTimeout(() => {
          isAnotherDateSelectionDialogOpen.value = false;
        }, 1000);
      })
      .catch(async (error: any) => {
        if (error.status >= 500) {
          isServerErrorDialogOpen.value = true;
        } else if (error.status !== 401) {
          const {
            isNeededVehicleFound: needed,
            isOutwardVehicleFound: outward,
            isReturnVehicleFound: ret,
            isAnotherDateSelectionDialogOpen: another,
            isServerErrorDialogOpen: server,
            isWriteConflict,
          } = resolveReservationError(error, clearTemporaryReservationDeadline);
          isNeededVehicleFound.value = needed;
          isOutwardVehicleFound.value = outward;
          isReturnVehicleFound.value = ret;
          isAnotherDateSelectionDialogOpen.value = another;
          isServerErrorDialogOpen.value = server;
          requestDelay += 10000;

          if (isWriteConflict && requestDelay <= maxRequestDelay) {
            createReservation(
              requestDelay,
              maxRequestDelay,
              requestDelay === 10000 ? 'checkout.spinnerText10SecondsDelay' : 'checkout.spinnerText20or30SecondsDelay',
            );
          } else if (isWriteConflict && requestDelay > maxRequestDelay) {
            setDefaultValuesToVehicleNotFoundDialog();
          }
        }
      });

    if (isNeededVehicleFound.value || isAnotherDateSelectionDialogOpen.value) {
      store.commit('common/setGlobalLoading', false);
      store.commit('common/setLabel', null);
    }
  }, requestDelay);
}

function setDefaultValuesToVehicleNotFoundDialog() {
  isNeededVehicleFound.value = false;
  isOutwardVehicleFound.value = false;
  isReturnVehicleFound.value = false;
  isAnotherDateSelectionDialogOpen.value = true;
}

function clearTemporaryReservationDeadline() {
  store.commit('booking/clearTemporaryReservationDeadline');
}

function fillDefaultPassengerDetails() {
  store.commit('booking/setLoggedInPassengerDetails', getUserDetailsForBookingDetails.value);
}

function toMainPage() {
  isAnotherDateSelectionDialogOpen.value = false;
  router.push({ name: 'Booking' });
}

async function saveAnotherPassenger() {
  await SUserService.updateProfile({
    anotherPassengerList: [
      {
        passengerId: uuidv4(),
        phone: getPassengerPhone.value,
        firstName: getPassengerFirstName.value.trim(),
        lastName: getPassengerLastName.value.trim(),
      }],
  });
}

function getBookingOrderModel() {
  return {
    bookingRequestId: getBookingRequestId.value,
    flightNumber: getCheckoutInfo.value.FlightDetails,
    locale: route.value.params.lang,
    nightRiderCards: validNightCards.value,
    outwardTripComment: getCheckoutInfo.value.commentForOutward,
    anotherPersonIsRiding: isAnotherPassenger.value,
    passengerDetails: [{
      phone: getPassengerPhone.value,
      firstName: getPassengerFirstName.value.trim(),
      lastName: getPassengerLastName.value.trim(),
      email: getPassengerEmail.value,
    }],
    paymentType: getPaymentType.value,
    returnTripComment: getCheckoutInfo.value.commentForReturn,
  };
}

async function bookNow() {
  if (getIsFreeTrip.value) {
    store.commit('booking/setPaymentType', SPaymentTypeEnum.NO_PAYMENT);
  }

  trackGtmEvent('Book now button click', 'Button', 'Click', 'Booking confirmation');

  (vm.$refs as any).checkout.validate().then(async (success: boolean) => {
    if (!success) {
      return;
    }

    trackGtmEcommerceEvent('begin_checkout', getTotalPrice.value, isRoundTrip.value);
    nextTick(async () => {
      store.commit('common/setGlobalLoading', true);
      try {
        if (isAnotherPassenger.value && !getAnotherPassengerList.value.length) {
          saveAnotherPassenger();
        }

        const bookingOrderModel = getBookingOrderModel();

        const now = new Date().valueOf();
        const { data } = await SOrderService.bookingOrderCreate(bookingOrderModel);
        paymentInfo.value = data;

        const refreshedRemainingTime = Math.round((data.reservedTill - now) / 1000);

        store.commit('booking/setRemainingTime', refreshedRemainingTime);
        store.commit('common/setGlobalLoading', false);

        const isPaymentTypeRequiringMethod = getPaymentType.value === SPaymentTypeEnum.PRE_PAID
          || getPaymentType.value === SPaymentTypeEnum.PRE_AUTHORIZATION
          || getPaymentType.value === SPaymentTypeEnum.PRE_PAID_STRIPE;

        if (!getPaymentMethod.value && isPaymentTypeRequiringMethod) {
          isPaymentDialogOpen.value = true;
        } else if (getPaymentMethod.value && isPaymentTypeRequiringMethod) {
          // For Stripe with saved payment method, confirm payment using Payment Intent
          if (getPaymentType.value === SPaymentTypeEnum.PRE_PAID_STRIPE) {
            await confirmStripePaymentWithSavedMethod(data);
          } else {
            // For other payment types with saved method, proceed directly
            await onPaymentSuccess({ nonce: '' });
          }
        } else {
          await onPaymentSuccess({ nonce: '' });
        }
      } catch (e: any) {
        store.commit('common/setGlobalLoading', false);
        if (e.status !== 401) {
          if (e.errorCode === SCheckoutErrorsEnum.D2D_BOOKING_PAYMENT_TYPE_NOT_ALLOWED) {
            isOnBoardPaymentAvailableDialogOpen.value = true;
          } else {
            await setGlobalError(e, true, { isErrorBookingConfirmation: true });
          }
        }
      }
    });
  });
}

async function confirmStripePaymentWithSavedMethod(paymentData: any) {
  try {
    const clientSecret = paymentData?.stripePaymentSecret?.clientSecret;
    const publishableKey = paymentData?.stripePaymentSecret?.publishableKey;
    const paymentMethodId = getPaymentMethod.value;

    if (!clientSecret || !publishableKey || !paymentMethodId) {
      throw new Error('Stripe payment information is incomplete');
    }

    store.commit('common/setGlobalLoading', true);

    // Load Stripe
    const stripe = await loadStripe(publishableKey);

    // Confirm payment with saved payment method
    // Similar to StripeForm.vue confirmCardPayment approach
    const { error, paymentIntent } = await stripe!.confirmCardPayment(
      clientSecret,
      {
        payment_method: paymentMethodId,
      },
      {
        handleActions: false, // Don't handle 3D Secure automatically
      },
    );

    if (error) {
      store.commit('common/setGlobalLoading', false);

      // Show error to user
      await setGlobalError(
        { message: error.message || 'Payment failed. Please try again.' },
        true,
        { isErrorBookingConfirmation: true },
      );
      return;
    }

    // Check if payment requires additional action (3D Secure, etc.)
    if (paymentIntent!.status === 'requires_action' || (paymentIntent as any).status === 'requires_source_action') {
      // Handle 3D Secure or other required actions
      const { error: actionError } = await stripe!.confirmCardPayment(clientSecret);

      if (actionError) {
        store.commit('common/setGlobalLoading', false);
        await setGlobalError(
          { message: actionError.message || 'Payment authentication failed.' },
          true,
          { isErrorBookingConfirmation: true },
        );
        return;
      }
    }

    // Proceed with booking completion
    await onPaymentSuccess({ nonce: '' });
  } catch (e: any) {
    store.commit('common/setGlobalLoading', false);
    await setGlobalError(e, true, { isErrorBookingConfirmation: true });
  }
}

function onSignInSuccess() {
  if (getAuthToken.value && !getReservationId.value) {
    fillDefaultPassengerDetails();
    store.commit('common/setGlobalLoading', true);
    const requestDelay = 0;
    const maxRequestDelay = 30000;
    createReservation(requestDelay, maxRequestDelay, 'checkout.spinnerText');
  }
}

async function onNightCardLinkedInCheckout() {
  store.commit('common/setGlobalLoading', true);
  try {
    await store.dispatch('auth/populateUserProfileInfo');
    await store.dispatch('booking/cancelTemporaryReservation');
    await store.dispatch('booking/setTemporaryReservation', getBookingRequestId.value);
  } catch (e: any) {
    if (e?.status !== 401) {
      await setGlobalError(e, false);
    }
  }
  store.commit('common/setGlobalLoading', false);
}

onSignInSuccess();

onBeforeUnmount(() => {
  clearInterval(checkOrderStateIntervalId.value as ReturnType<typeof setInterval>);
});

return { __sfc: true,store, router, route, vuetify, vm, setGlobalError, trackGtmEvent, trackGtmEcommerceEvent, isLoading, isAgree, isAnotherDateSelectionDialogOpen, isTripDetailsOpen, isNeededVehicleFound, isOutwardVehicleFound, isReturnVehicleFound, isPaymentDialogOpen, isOnBoardPaymentAvailableDialogOpen, isServerErrorDialogOpen, paymentInfo, validNightCards, checkOrderStateIntervalId, getTripType, getAuthToken, isFreeTrip, getSelectedRides, getPaymentType, getPaymentMethod, getIsFreeTrip, getBookingRequestId, getPassengerPhone, getPassengerFirstName, getPassengerLastName, getPassengerEmail, getUserDetailsForBookingDetails, getCheckoutInfo, getOutwardState, getReturnState, getReservationId, isAnotherPassenger, getAnotherPassengerList, getBookingDetails, isRoundTrip, isExtraSmall, isCheckoutPage, getTotalPrice, onTrackEventNoVehiclesFound, onTrackEventServerErrorOccurred, onTrackEventPayButtonClick, updateNightCards, paymentCompleted, subscribeOnPaymentComplete, onPaymentSuccess, createReservation, setDefaultValuesToVehicleNotFoundDialog, clearTemporaryReservationDeadline, fillDefaultPassengerDetails, toMainPage, saveAnotherPassenger, getBookingOrderModel, bookNow, confirmStripePaymentWithSavedMethod, onSignInSuccess, onNightCardLinkedInCheckout, SOnBoardPaymentErrorDialog, SServerErrorDialog, SMobileBookingSummary, SCheckoutBookingSummary, SVehicleSearchResultDialog, SPaymentDialog, PassengerInfo }
}

})